import { apiCall } from "api-calls/api-call"
import { SourceGameEnum } from "domains/accountsBuffPlay/constants/source-options"
import {
  BonusSpendMission,
  BonusSpendMissionTier,
  CreateDailyMissionRequest,
  CreateGameTask,
  CreateNativeGameRequest,
  DailyMission,
  GamePlatformType,
  GamePublisherOption,
  GameTask,
  NativeGame,
  NativeGameFilter,
  UpdateDailyMissionRequest,
} from "../store/types"

export const getAvailableNativeGames = async(
  page: number,
  limit: number,
  filter: NativeGameFilter,
) => {
  // Defaults live here so the first fetch is scoped (NATIVE, active-only)
  // even when the store hasn't seeded `filter` yet — the store deliberately
  // does NOT pre-populate filter to avoid an eager fetch on app boot.
  const query: {
    page: number;
    limit: number;
    name?: string;
    activeGames: boolean;
    gameSource: string;
  } = {
    limit,
    page,
    activeGames: filter?.hideDeleted ?? true,
    gameSource: filter?.gameSource ?? SourceGameEnum.NATIVE,
  }
  if (filter?.name) query.name = filter.name
  const response = await apiCall({
    method: "GET",
    path: "buff-play/games",
    query,
  })
  if (response.data?.length) {
    response.data.forEach((game: NativeGame) => {
      if (game.tasks?.length) {
        game.tasks.sort((a, b) => a.sequence - b.sequence)
      }
    })
  }

  return response
}

export const editNativeGameTask = async(
  gameId: string,
  taskId: string,
  data: Partial<GameTask>,
) => {
  return apiCall({
    method: "PATCH",
    path: `buff-play/games/${gameId}/tasks/${taskId}`,
    body: data,
  })
}

export const createNativeGameTask = async(gameId: string, data: CreateGameTask) => {
  return apiCall({
    method: "POST",
    path: `buff-play/games/${gameId}/tasks`,
    body: data,
  })
}

export const updateNativeGame = async(
  id: string,
  data: Partial<NativeGame>,
) => {
  return apiCall({
    method: "PUT",
    path: `buff-play/games/${id}`,
    body: data,
  })
}

export const deleteNativeGame = async(id: string) => {
  return apiCall({
    method: "DELETE",
    path: `buff-play/games/${id}`,
  })
}

export const createNativeGame = async(data: CreateNativeGameRequest) => {
  return apiCall({
    method: "POST",
    path: "buff-play/games",
    body: data,
  })
}

export const restoreNativeGame = async(id: string) => {
  return apiCall({
    method: "PATCH",
    path: `buff-play/games/${id}/restore`,
  })
}

export const getBonusSpendMissions = async(
  gameId: string,
  platform: GamePlatformType,
): Promise<BonusSpendMission[]> => {
  const response = await apiCall({
    method: "GET",
    path: `admin/games/${gameId}/bonus-missions`,
    query: { platform },
  })

  return response || []
}

export const setBonusSpendMissions = async(
  gameId: string,
  platform: GamePlatformType,
  tiers: BonusSpendMissionTier[],
): Promise<BonusSpendMission[]> => {
  const response = await apiCall({
    method: "PUT",
    path: `admin/games/${gameId}/bonus-missions`,
    body: { platform, tiers },
  })

  return response || []
}

export const getDailyMissions = async(
  gameId: string,
  platform: GamePlatformType,
): Promise<DailyMission[]> => {
  const response = await apiCall({
    method: "GET",
    path: `admin/games/${gameId}/daily-missions`,
    query: { platform },
  })

  return response || []
}

export const createDailyMission = async(data: CreateDailyMissionRequest): Promise<DailyMission> => {
  return apiCall({
    method: "POST",
    path: "admin/daily-missions",
    body: data,
  })
}

export const updateDailyMission = async(
  id: string,
  data: UpdateDailyMissionRequest,
): Promise<DailyMission> => {
  return apiCall({
    method: "PUT",
    path: `admin/daily-missions/${id}`,
    body: data,
  })
}

export const deleteDailyMission = async(id: string): Promise<{ success: true }> => {
  return apiCall({
    method: "DELETE",
    path: `admin/daily-missions/${id}`,
  })
}

export const getGamePublishers = async(): Promise<GamePublisherOption[]> => {
  const response = await apiCall({
    method: "GET",
    path: "buff-play/game-publishers",
    query: { page: 1, limit: 100 },
  })

  return response.data?.map((p: any) => ({ id: p.id, name: p.name })) || []
}

